/**
 * usePresence & useIsPresent
 *
 * Custom React hooks for use with <AnimatePresence>.
 *
 * usePresence:
 * - Returns `isPresent` and `safeToRemove`
 * - Registers the component so AnimatePresence waits for safeToRemove() before unmounting
 * - Use when your component has an exit animation
 *
 * useIsPresent:
 * - Returns only `isPresent` (boolean)
 * - Does NOT register - AnimatePresence won't wait for this component
 * - Use when you just need to react to presence state without blocking removal
 *
 * Usage:
 * ```tsx
 * // With exit animation (blocks removal until safeToRemove called)
 * const { isPresent, safeToRemove } = usePresence();
 *
 * // Just reading presence state (doesn't block removal)
 * const isPresent = useIsPresent();
 * ```
 *
 * Throws if used outside of <AnimatePresence>.
 */

import { useCallback, useContext, useEffect, useRef } from "react"

import { PresenceContext } from "./PresenceContext"

export type UsePresenceResult = {
  /**
   * Whether this component is still considered present.
   * false = exiting (but still mounted)
   */
  isPresent: boolean

  /**
   * Call when exit animation is fully complete.
   * This signals AnimatePresence that this component is done animating.
   * AnimatePresence waits for ALL registered children to call safeToRemove before unmounting.
   */
  safeToRemove: () => void
}

/**
 * Returns only the presence state without registering for exit animation tracking.
 * Use this when you need to know if the component is exiting but don't need to
 * block AnimatePresence from removing the element.
 */
export function useIsPresent(): boolean {
  const context = useContext(PresenceContext)

  if (!context) {
    throw new Error(
      "useIsPresent must be used within an <AnimatePresence> component",
    )
  }

  return context.isPresent
}

/**
 * Returns presence state and a safeToRemove callback.
 * Registers the component so AnimatePresence will wait for safeToRemove()
 * to be called before unmounting.
 */
export function usePresence(): UsePresenceResult {
  const context = useContext(PresenceContext)

  if (!context) {
    throw new Error(
      "usePresence must be used within an <AnimatePresence> component",
    )
  }

  const { isPresent, register } = context
  const safeToRemoveRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    const unregister = register()
    safeToRemoveRef.current = unregister

    return () => {
      // If component unmounts before calling safeToRemove, auto-unregister
      if (safeToRemoveRef.current) {
        safeToRemoveRef.current()
        safeToRemoveRef.current = null
      }
    }
  }, [register])

  const safeToRemove = useCallback(() => {
    if (safeToRemoveRef.current) {
      safeToRemoveRef.current()
      safeToRemoveRef.current = null
    }
  }, [])

  return {
    isPresent,
    safeToRemove,
  }
}
